// Generic inline card for device types without a dedicated control card, so the
// grid NEVER opens a side window. It fetches the device's outputs: if there are
// on/off relays it renders a toggle per output (+ All On/Off); otherwise (or when
// infoOnly, e.g. read-only energy meters) it shows a compact info tile.

import React, { useEffect, useState } from 'react';
import { Card, Box, Typography, Button } from '@mui/material';
import PowerSettingsNewIcon from '@mui/icons-material/PowerSettingsNew';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import DevicesOtherIcon from '@mui/icons-material/DevicesOther';
import BoltIcon from '@mui/icons-material/Bolt';
import { useApp } from '../../../core/state/AppContext';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { parseStatus } from '../../../core/mqtt/mqttClient';
import { publishCommand } from '../../../core/utils/deviceController';
import { deviceTypeKey } from '../../../core/devices/deviceMeta';
import { getOutputs, getDeviceStatus } from '../../../core/api/api';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';
import { panelGridSx, channelButtonSx, CARD_TYPE } from '../../../core/devices/buttonLayout';
// ⚠️ THE LADDER COMES FROM statusFormat, NOT core/devices/fan. There are two,
// and they are not interchangeable: fan.js carries the Flutter-era SIX-step
// ladder ({10,20,40,60,80,100}) for the standalone fan reached over MQTT, while
// a 7+1's fan has EIGHT running steps — off plus {30,40,50,60,70,80,90,100},
// index 8 being max. Using the six-step one here leaves a fan running at 30,
// 50, 70 or 90 matching no button at all, so the panel shows nothing selected
// while the fan is plainly turning.
import {
  fanChannelsFor, masterChannelsFor, masterCommandFor,
  FAN_LADDER, FAN_OFF, fanStepLabelOf,
} from '../../../core/devices/statusFormat';

// ⚠️ THE TRAILING LEVEL CHANNELS ARE NOT BOOLEANS. A 7+1's socket reports
// 10…100, so `=== '1'` made it false at every level including full — the reason
// that tile could never light up. Those slots keep their raw number; the rest
// stay booleans.
function parseStates(status, n, fans = 0) {
  if (!status) return Array(n).fill(false);
  if (n === 1) {
    const p = status.split('-');
    return [p[p.length - 1] !== '0'];
  }
  const p = status.split(',');
  const firstLevel = n - fans;
  return Array.from({ length: n }, (_, i) => (
    i >= firstLevel ? (Number(p[i]) || FAN_OFF) : p[i] === '1'
  ));
}

export default function GenericControlCard({ device, subtitle, infoOnly = false }) {
  const { userId } = useApp();
  const uno = device.UniqueNumber;
  const [outputs, setOutputs] = useState([]);
  const [loaded, setLoaded] = useState(infoOnly);
  // Seed from the already-loaded device row (list includes `Status`) so states
  // reflect reality immediately; live MQTT then keeps them current.
  const [status, setStatus, liveAt] = useDeviceStatus(uno, parseStatus(device.Status || device.DeviceStatus || ''));
  const [states, setStates] = useState([]);

  useEffect(() => {
    if (infoOnly) return undefined;
    let alive = true;
    (async () => {
      const [out, st] = await Promise.all([
        getOutputs(uno).catch(() => null),
        getDeviceStatus(uno).catch(() => null),
      ]);
      if (!alive) return;
      const list = out && Array.isArray(out.data) ? out.data : [];
      setOutputs(list);
      setStates(Array(list.length).fill(false));
      const row = st && Array.isArray(st.data) ? st.data[0] : null;
      if (row && row.Status) {
        const db = parseStatus(row.Status);
        if (db) setStatus((cur) => cur || db);
      }
      setLoaded(true);
    })();
    return () => { alive = false; };
  }, [uno, infoOnly, setStatus]);

  // Declared before the effect below, which reads `fans` in its dependency
  // array — evaluated during render, so a later `const` would be in the TDZ.
  const fans = fanChannelsFor(device.DeviceID);
  const masters = masterChannelsFor(device.DeviceID);

  useEffect(() => {
    if (status && outputs.length) setStates(parseStates(status, outputs.length, fans));
  }, [status, outputs.length, fans]);

  const n = outputs.length;

  // ── What this panel is actually made of ──────────────────────────────────
  // A 7+1 is NOT eight switches. Its glass has seven switch buttons and, at the
  // bottom right, a MASTER; its eighth OUTPUT is a dimmable socket, which is not
  // a touch position at all. So the tile grid is 7 switches + master, and the
  // socket gets its own level control underneath.
  //
  // Drawn as eight plain toggles — which is what happened before — the socket
  // reported OFF at every level (isOn tests === 1, the socket reports 10…100)
  // and tapping it sent "8-1", a duty of 1 rather than "on".
  const switchCount = Math.max(0, n - fans);
  // The master takes the slot after the switches, which on a two-row grid is
  // the bottom right — where it is on the wall.
  const slots = switchCount + (masters > 0 ? 1 : 0);
  const masterSlot = masters > 0 ? switchCount : -1;
  // Its own state is never reported (see statusFormat.MASTER_CHANNELS_BY_TYPE),
  // so it lights from the switches it governs.
  const anyOn = states.slice(0, switchCount).some(Boolean);

  const pressMaster = () => {
    const cmd = masterCommandFor(states.slice(0, switchCount));
    setStates((prev) => prev.map((v, i) => (i < switchCount ? cmd === 'allon' : v)));
    publishCommand(uno, cmd, userId);
  };

  const setFan = (ch, speed) => {
    setStates((prev) => { const u = [...prev]; u[ch - 1] = speed; return u; });
    publishCommand(uno, `${ch}-${speed}`, userId);
  };

  const toggle = (i) => {
    const next = !states[i];
    setStates((prev) => { const u = [...prev]; u[i] = next; return u; });
    publishCommand(uno, `${i + 1}-${next ? 1 : 0}`, userId);
  };
  const setAll = (on) => {
    setStates(Array(n).fill(on));
    publishCommand(uno, on ? 'allon' : 'alloff', userId);
  };
  const outName = (i) => {
    const o = outputs.find((x) => Number(x.Output) === i + 1);
    return (o && o.OutputName) || `Switch ${i + 1}`;
  };

  const header = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        {subtitle && (
          <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>{subtitle}</Typography>
        )}
        <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>{device.DeviceName || uno}</Typography>
      </Box>
      <DeviceStatusDot device={device} live={liveAt} />
      <DeviceCardMenu device={device} />
    </Box>
  );

  const infoTile = () => {
    const meter = deviceTypeKey(device.DeviceID) === 'singlePhase' || deviceTypeKey(device.DeviceID) === 'threePhase';
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5, flex: 1, py: 2, color: 'text.secondary' }}>
        {meter ? <BoltIcon fontSize="large" /> : <DevicesOtherIcon fontSize="large" />}
        <Typography variant="caption">{meter ? 'Read-only meter' : 'No controllable outputs'}</Typography>
      </Box>
    );
  };

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 0.75, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      {header}

      {(!loaded) ? (
        <Box sx={{ flex: 1 }} />
      ) : n === 0 ? (
        infoTile()
      ) : (
        <>
          {/* Same panel grid as the touch card: this is the fallback for
              unmapped types, and a device landing here should not be laid out
              differently from the same panel on a mapped type. */}
          <Box sx={panelGridSx(slots)}>
            {Array.from({ length: slots }, (_, i) => {
              const master = i === masterSlot;
              const on = master ? anyOn : states[i];
              return (
                <Button
                  key={i}
                  onClick={() => (master ? pressMaster() : toggle(i))}
                  variant={on ? 'contained' : 'outlined'}
                  color={master ? 'primary' : (on ? 'success' : 'inherit')}
                  startIcon={master ? <SelectAllIcon /> : <PowerSettingsNewIcon />}
                  sx={channelButtonSx(on)}
                >
                  <Box sx={{ minWidth: 0, textAlign: 'left', lineHeight: 1.1 }}>
                    <Typography fontWeight={600} noWrap sx={{ fontSize: CARD_TYPE.channel }}>
                      {master ? 'Master' : outName(i)}
                    </Typography>
                    <Typography sx={{ opacity: 0.8, fontSize: CARD_TYPE.state }}>
                      {/* The master says what pressing it will DO, because it
                          has no state of its own to report. */}
                      {master ? (anyOn ? 'ALL OFF' : 'ALL ON') : (on ? 'ON' : 'OFF')}
                    </Typography>
                  </Box>
                </Button>
              );
            })}
          </Box>

          {/* The socket / fan channel: a LEVEL, not a switch. 10 is off. */}
          {fans > 0 && Array.from({ length: fans }, (_, f) => {
            const ch = switchCount + f + 1;
            const speed = Number(states[ch - 1]) || FAN_OFF;
            return (
              <Box key={ch} sx={{ mt: 0.5 }}>
                <Typography color="text.secondary" noWrap sx={{ display: 'block', fontSize: CARD_TYPE.channel }}>
                  {outName(ch - 1)}
                </Typography>
                {/* Off plus all eight running steps — the whole ladder the
                    device can report, so every value it sends has a button that
                    can light up. */}
                <Box sx={{ display: 'flex', gap: 0.375, flexWrap: 'wrap' }}>
                  {FAN_LADDER.map((sp) => (
                    <Button
                      key={sp}
                      size="small"
                      onClick={() => setFan(ch, sp)}
                      variant={speed === sp ? 'contained' : 'outlined'}
                      color={speed === sp ? 'success' : 'inherit'}
                      sx={{
                        minWidth: 0, px: 0.5, py: 0.25, borderRadius: 2, flex: '1 0 auto',
                      }}
                    >
                      <Typography fontWeight={700} sx={{ fontSize: CARD_TYPE.state }}>
                        {fanStepLabelOf(sp)}
                      </Typography>
                    </Button>
                  ))}
                </Box>
              </Box>
            );
          })}
          {/* Hidden when the panel has a master: that tile IS this control, in
              the place the wall puts it, and two rows doing the same thing
              invite the question of how they differ. */}
          {n > 1 && masters === 0 && (
            <Box sx={{ display: 'flex', gap: 1, mt: 'auto' }}>
              <Button size="small" fullWidth variant="contained" onClick={() => setAll(true)} sx={{ fontSize: CARD_TYPE.action, py: 0.25 }}>All On</Button>
              <Button size="small" fullWidth variant="outlined" onClick={() => setAll(false)} sx={{ fontSize: CARD_TYPE.action, py: 0.25 }}>All Off</Button>
            </Box>
          )}
        </>
      )}

    </Card>
  );
}
